import React, { useState, Suspense, lazy, useEffect } from 'react' 
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom' 
import { initializePerformanceOptimizations } from './utils/performanceOptimizationService' 
import { initializeCriticalCSS } from './utils/criticalCSS' 
import './utils/firebaseOptimization' 
import { AuthProvider, useAuth } from './context/AuthContext' 
import { CartProvider, useCart } from './context/CartContext' 
import { CustomerProvider } from './context/CustomerContext' 
import { NotificationProvider } from './context/NotificationContext' 
import { UserPreferencesProvider } from './context/UserPreferencesContext' 
import { MaintenanceProvider, useMaintenance } from './context/MaintenanceContext' 
import { ViewModeProvider, useViewMode } from './context/ViewModeContext' 
import { HeroProvider } from './context/HeroContext' 
import { FavoritesProvider } from './context/FavoritesContext' 
import { CartAnimationProvider } from './context/CartAnimationContext' 
import { CurrencyProvider } from './context/CurrencyContext' 
import { WishlistProvider } from './context/WishlistContext' 
import { LocalizationProvider, useLocalization } from './context/LocalizationContext' 
import { ComingSoonProvider, useComingSoon } from './context/ComingSoonContext' 
import { DevelopmentProvider, useDevelopment } from './context/DevelopmentContext' 
import { PushNotificationProvider } from './context/PushNotificationContext' 
import './i18n' 
import HomePageFallback from './components/HomePageFallback'
import { CookieConsentProvider } from './context/CookieConsentContext'
// import PWAUpdateTest from './components/PWAUpdateTest' // Component not found 
import ProtectedRoute from './components/ProtectedRoute' 
import { ToastProvider } from './components/NotificationToast'
import { usePWA } from './hooks/usePWA' 
import './App.css';

const HomePage = lazy(() => import('./components/HomePage'));
const NewNavbar = lazy(() => import('./components/Navbar'));
const CartAnimation = lazy(() => import('./components/CartAnimation'));
const GiftIntegration = lazy(() => import('./components/GiftIntegration'));
const Footer = lazy(() => import('./components/Footer'));
const BlackFridayBanner = lazy(() => import('./components/BlackFridayBanner'));
const CookieConsentModal = lazy(() => import('./components/CookieConsentModal'));
const SideCart = lazy(() => import('./components/SideCart'));
const PWAInstallPrompt = lazy(() =>
  import('./components/PWAInstallPrompt').then((m) => ({ default: m.PWAInstallPrompt }))
);
const PWAUpdateNotification = lazy(() =>
  import('./components/PWAUpdateNotification').then((m) => ({ default: m.PWAUpdateNotification }))
);
const ScrollToTop = lazy(() => import('./components/ScrollToTop'));
const DeferOnMobile = lazy(() => import('./components/DeferOnMobile'));
const ChatBubble = lazy(() => import('./components/ChatBubble'));
const SupportAssistantBubble = lazy(() => import('./components/SupportAssistantBubble'));
const NetworkStatusToasts = lazy(() => import('./components/NetworkStatusToasts'));
const DispatchVideoNotifier = lazy(() => import('./components/DispatchVideoNotifier'));
const CustomerMessageToastNotifier = lazy(() => import('./components/CustomerMessageToastNotifier'));
const ProductDetailsPage = lazy(() => import('./components/ProductDetailsPage'));
const BundleDetailPage = lazy(() => import('./components/BundleDetailPage'));
const ProductCarousel = lazy(() => import('./components/ProductCarousel'));
const OptimizedProductCollection = lazy(() => import('./components/OptimizedProductCollection'));
const SimpleProductCollection = lazy(() => import('./components/SimpleProductCollection'));
const StringsSection = lazy(() => import('./components/StringsSection'));
const GuestCheckoutForm = lazy(() => import('./components/GuestCheckoutForm'));
const BrandManager = lazy(() => import('./components/BrandManager'));
const BrandingManager = lazy(() => import('./components/BrandingManager'));
const PopularBrands = lazy(() => import('./components/PopularBrands'));
const BrandCollection = lazy(() => import('./components/BrandCollection'));
const ErnieBallCollection = lazy(() => import('./components/ErnieBallCollection'));
const UkuleleStringsPage = lazy(() => import('./components/UkuleleStringsPage'));
const DAddarioCollection = lazy(() => import('./components/DAddarioCollection'));
const ElixirCollection = lazy(() => import('./components/ElixirCollection'));
const MusicVideoGrid = lazy(() => import('./components/MusicVideoGrid'));
const VideoPerformancesPage = lazy(() => import('./components/VideoPerformancesPage'));
const StringFinderQuiz = lazy(() => import('./components/StringFinderQuiz'));
const StringFinderDemo = lazy(() => import('./components/StringFinderDemo'));
const EnhancedSearch = lazy(() => import('./components/EnhancedSearch'));
const MostPopularPage = lazy(() => import('./components/MostPopularPage'));
const RefundPolicy = lazy(() => import('./components/RefundPolicy'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const ShippingReturnPolicy = lazy(() => import('./components/ShippingReturnPolicy'));
const ComparativeAdvertisingDisclaimer = lazy(() => import('./components/ComparativeAdvertisingDisclaimer'));
const LearningHub = lazy(() => import('./components/LearningHub'));
const LearningHubAdmin = lazy(() => import('./components/admin/LearningHubAdmin'));
const PromptLibraryAdmin = lazy(() => import('./components/promptLibrary/PromptLibraryAdmin'));
const StoryboardManager = lazy(() => import('./components/storyboard/StoryboardManager'));
const AIProductAssistant = lazy(() => import('./components/AIProductAssistant'));
const CheckoutPage = lazy(() => import('./components/CheckoutPage'));
const CheckoutSuccess = lazy(() => import('./components/CheckoutSuccess'));
const UserDashboard = lazy(() => import('./components/UserDashboard'));
const CustomerMessages = lazy(() => import('./components/CustomerMessages'));
const CustomerChat = lazy(() => import('./components/CustomerChat'));
const CustomerOrders = lazy(() => import('./components/CustomerOrders'));
const AdminChat = lazy(() => import('./components/AdminChat'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const ShopifyDashboard = lazy(() => import('./components/ShopifyDashboard'));
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const AdminSetup = lazy(() => import('./components/AdminSetup'));
const AdminRoleFix = lazy(() => import('./components/AdminRoleFix'));
const NavbarDebug = lazy(() => import('./components/NavbarDebug'));
const CustomerRoleFix = lazy(() => import('./components/CustomerRoleFix'));
// const CustomerIntegrationTest = lazy(() => import('./components/CustomerIntegrationTest')); // Component not found
const ProductManager = lazy(() => import('./components/ProductManager')); 
const ProductManagement = lazy(() => import('./components/ProductManagement'));
const BlackFridayManager = lazy(() => import('./components/BlackFridayManager'));
const HolidaySpecialsManager = lazy(() => import('./components/HolidaySpecialsManager'));
const MerchManagement = lazy(() => import('./components/MerchManagement'));
const MerchStore = lazy(() => import('./components/MerchStore'));
const ManageActiveProducts = lazy(() => import('./components/ManageActiveProducts'));
const MixAndMatch = lazy(() => import('./components/MixAndMatch'));
const BuildYourSet = lazy(() => import('./components/BuildYourSet'));
const Wishlist = lazy(() => import('./components/Wishlist'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const MixAndMatchManagement = lazy(() => import('./components/MixAndMatchManagement')); 
const BundleDealsManager = lazy(() => import('./components/BundleDealsManager' ));
const VideoPerformanceManager = lazy(() => import('./components/VideoPerformanceManager'));
const AddAcousticStrings = lazy(() => import('./components/AddAcousticStrings' ));
const AdminAnalytics = lazy(() => import('./components/AdminAnalytics' ));
const AdminSettings = lazy(() => import('./components/AdminSettings' ));
const AdminSettingsPanel = lazy(() => import('./components/AdminSettingsPanel' ));
const PaymentConfigManager = lazy(() => import('./components/PaymentConfigManager' ));
const ShippingManagement = lazy(() => import('./components/ShippingManagement' ));
const AdminNotificationCenter = lazy(() => import('./components/AdminNotificationCenter' ));
const AdminTrackingManagement = lazy(() => import('./components/AdminTrackingManagement' ));
const AdminSystemMonitor = lazy(() => import('./components/AdminSystemMonitor' ));
const ABTestAdmin = lazy(() => import('./components/ABTestAdmin' ));
const MusicAdminPage = lazy(() => import('./components/MusicAdminPage' ));
const OrderManagement = lazy(() => import('./components/OrderManagement' ));
const CustomerMessaging = lazy(() => import('./components/CustomerMessaging' ));
const MessageCenter = lazy(() => import('./components/MessageCenter' ));
const CustomerSupport = lazy(() => import('./components/CustomerSupport' ));
// const MessageTest = lazy(() => import('./components/MessageTest' )); // Component not found
// const MessageFlowTest = lazy(() => import('./components/MessageFlowTest' )); // Component not found
const MessageDiagnostic = lazy(() => import('./components/MessageDiagnostic' ));
// const MessageTestData = lazy(() => import('./components/MessageTestData' )); // Component not found
const CustomerManagement = lazy(() => import('./components/CustomerManagement' ));
const EnhancedNotificationSystem = lazy(() => import('./components/EnhancedNotificationSystem' ));
const SalesAnalyticsDashboard = lazy(() => import('./components/SalesAnalyticsDashboard' ));
const EnhancedAnalytics = lazy(() => import('./components/EnhancedAnalytics' ));
const EnhancedInventoryManager = lazy(() => import('./components/EnhancedInventoryManager' ));
const SettingsPanel = lazy(() => import('./components/SettingsPanel' ));
const ComingSoonAdmin = lazy(() => import('./components/ComingSoonAdmin' ));
const MaintenancePage = lazy(() => import('./components/MaintenancePage' ));
const MaintenanceAdminPage = lazy(() => import('./components/MaintenanceAdminPage' ));
const LoginPage = lazy(() => import('./components/LoginPage' ));
const Auth = lazy(() => import('./components/Auth' ));
const SignUpPage = lazy(() => import('./components/SignUpPage' ));
const ContactPage = lazy(() => import('./components/ContactPage' ));
const GiftManager = lazy(() => import('./components/GiftManager' ));
const CategoryManager = lazy(() => import('./components/CategoryManager' ));
const FlashSalesManager = lazy(() => import('./components/FlashSalesManager' ));
const VolumePricingManager = lazy(() => import('./components/VolumePricingManager' ));
const DiscountManagement = lazy(() => import('./components/DiscountManagement' ));
const MegaMenuManager = lazy(() => import('./components/MegaMenuManager' ));
const FunnelEngineAdmin = lazy(() => import('./components/FunnelEngineAdmin' ));
const FunnelLandingPage = lazy(() => import('./components/FunnelLandingPage' ));
const FunnelsAdmin = lazy(() => import('./components/funnel/FunnelsAdmin' ));
const AutomatedMessaging = lazy(() => import('./components/admin/AutomatedMessaging' ));
const CustomerOrderHistory = lazy(() => import('./components/CustomerOrderHistory'));
const FunnelEditPage = lazy(() => import('./pages/admin/FunnelEditPage' ));

// Simple redirect to home page
const AuthPage: React.FC = () => {
  useEffect(() => {
    window.location.href = '/';
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg">Redirecting to home...</p>
      </div>
    </div>
  );
};

// Wrapper component that can access language context
const AppContent: React.FC = () => {
  const { language } = useLocalization();
  const [nonCriticalUiReady, setNonCriticalUiReady] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  });
  const { isComingSoonEnabled, toggleComingSoon, loading } = useComingSoon();
  const { isLocalDevelopment, shouldShowComingSoon } = useDevelopment();
  const { isMaintenanceEnabled, maintenanceSettings, toggleMaintenance, loading: maintenanceLoading } = useMaintenance();
  const { isCustomerOnlyMode } = useViewMode();
  const { isInstalled, hasUpdate, updateApp } = usePWA();
  const { currentUser, userRole } = useAuth();
  const location = useLocation();
  
  // Defer non-critical init to idle to reduce TBT; longer timeout on mobile so it runs after first paint
  useEffect(() => {
    const run = () => {
      initializePerformanceOptimizations();
      initializeCriticalCSS();
    };
    const isMobile = () => typeof window !== 'undefined' && (window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    const timeout = isMobile() ? 3500 : 2000;
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(run, { timeout });
    } else {
      setTimeout(run, 1);
    }
  }, []);
  
  // Check if current route is an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');
  // Check if current route is an admin login/auth route
  const isAdminAuthRoute = location.pathname === '/auth' || 
                           location.pathname === '/admin/login' || 
                           location.pathname === '/sp-secure-admin-access-2024' ||
                           location.pathname === '/a2025' ||
                           location.pathname === '/admin/a2025';

  // Defer Font Awesome CSS so it's not in the initial bundle (reduces unused CSS on first load)
  useEffect(() => {
    const load = () => {
      import(/* webpackChunkName: "fa-css" */ '@fortawesome/fontawesome-free/css/all.min.css');
    };
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(load, { timeout: 2000 });
    } else {
      setTimeout(load, 500);
    }
  }, []);

  // Hide top banner reserve on admin routes to avoid empty gap
  useEffect(() => {
    if (isAdminRoute || isAdminAuthRoute || isMobileViewport) {
      document.body.classList.add('hide-banner-reserve');
    } else {
      document.body.classList.remove('hide-banner-reserve');
    }
    return () => document.body.classList.remove('hide-banner-reserve');
  }, [isAdminRoute, isAdminAuthRoute, isMobileViewport]);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobileViewport(mobile);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Defer non-critical UI mounts (banner/chat/cookie) to reduce desktop TBT.
  useEffect(() => {
    const mount = () => setNonCriticalUiReady(true);
    if (typeof requestIdleCallback !== 'undefined') {
      const id = requestIdleCallback(mount, { timeout: 1200 });
      return () => cancelIdleCallback(id);
    }
    const t = setTimeout(mount, 250);
    return () => clearTimeout(t);
  }, []);
  
  // Check if user is an admin
  const isAdminUser = userRole === 'admin';
  
  // In customer-only mode, redirect admin routes to home
  useEffect(() => {
    if (isCustomerOnlyMode && isAdminRoute) {
      window.location.href = '/';
    }
  }, [isCustomerOnlyMode, isAdminRoute]);
  
  // Show maintenance page if enabled and NOT on admin routes
  // With localStorage cache, state is available immediately, preventing flash
  // Show maintenance page if enabled (from cache or Firebase) or during initial load if cached as enabled
  if (!isAdminRoute && isMaintenanceEnabled) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>}>
        <MaintenancePage 
          isEnabled={isMaintenanceEnabled} 
          onToggle={toggleMaintenance}
          message={maintenanceSettings.message}
          estimatedTime={maintenanceSettings.estimatedTime}
        />
      </Suspense>
    );
  }
  
  // Coming Soon page is disabled - only show maintenance page
  // DISABLED: Coming soon page functionality is disabled
  // const shouldShowComingSoonPage = isAdminRoute 
  //   ? false // Never show coming soon for admin routes
  //   : (isLocalDevelopment 
  //       ? shouldShowComingSoon 
  //       : (isComingSoonEnabled && !isAdminRoute) || loading);
  
  // Show coming soon page if enabled and NOT on admin routes, or while loading (to prevent flash)
  // DISABLED: Coming soon page is disabled - only maintenance page will show
  // if (shouldShowComingSoonPage) {
  //   logger.debug('✅ Showing Coming Soon Page (Development Mode:', isLocalDevelopment, ')');
  //   return (
  //     <ComingSoonPage 
  //       isEnabled={isComingSoonEnabled} 
  //       onToggle={toggleComingSoon} 
  //     />
  //   );
  // }
  
  return (
    <div className="App" key={language}>
      <CookieConsentProvider>
      <Suspense fallback={null}>
        {/* Black Friday Banner - Show on all non-admin routes */}
        {!isAdminRoute && !isAdminAuthRoute && !isMobileViewport && nonCriticalUiReady && <BlackFridayBanner />}
        {/* Show Navbar for all users on non-admin routes, except homepage and admin/auth pages which have their own navbar */}
        {!isAdminRoute && !isAdminAuthRoute && location.pathname !== '/' && <NewNavbar />}
        
        <ScrollToTop />
        {/* Defer non-critical global UI mounts to idle-ready phase to reduce startup work */}
        {nonCriticalUiReady && (
          <>
            <CartAnimation />
            <GiftIntegration />
            <SideCart />
          </>
        )}
        
        {/* AI assistant + live chat: home page only; cookie stays site-wide */}
        {!isAdminRoute && !isAdminAuthRoute && nonCriticalUiReady && (
          <DeferOnMobile>
            {location.pathname === '/' && (
              <>
                <SupportAssistantBubble />
                <ChatBubble />
              </>
            )}
            <CookieConsentModal />
          </DeferOnMobile>
        )}
        
        {/* PWA Components */}
        {nonCriticalUiReady && (
          <>
            <PWAInstallPrompt />
            <PWAUpdateNotification />
          </>
        )}
        {!isAdminRoute && !isAdminAuthRoute && nonCriticalUiReady && <NetworkStatusToasts />}
        {!isAdminRoute && !isAdminAuthRoute && nonCriticalUiReady && <DispatchVideoNotifier />}
        {!isAdminRoute && !isAdminAuthRoute && nonCriticalUiReady && <CustomerMessageToastNotifier />}
      </Suspense>
      {/* <PWAUpdateTest /> */}
      
      {/* Push Notification Permission Prompt */}
      {/* <NotificationPermissionModal /> */}
      
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>}>
        <Routes>
          <Route path="/" element={<Suspense fallback={<HomePageFallback />}><HomePage /></Suspense>} />
          <Route path="/strings" element={<StringsSection />} />
          <Route path="/products" element={<OptimizedProductCollection />} />
          <Route path="/products/:category" element={<SimpleProductCollection />} />
          <Route path="/merch" element={<MerchStore />} />
          <Route path="/merch-store" element={<MerchStore />} />
          <Route path="/search" element={<EnhancedSearch />} />
          <Route path="/most-popular" element={<MostPopularPage />} />
          <Route path="/ai-assistant" element={<AIProductAssistant />} />
          <Route path="/product/:productId" element={<ProductDetailsPage />} />
          <Route path="/bundle/:id" element={<BundleDetailPage />} />
          <Route path="/product-carousel/:productId?" element={<ProductCarousel />} />
          <Route path="/cart" element={<CartRedirect />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout-success" element={<CheckoutSuccess />} />
          {/* Login and Auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth" element={<Navigate to="/" replace />} />
          <Route path="/admin/login" element={<Navigate to="/" replace />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/shipping-return-policy" element={<ShippingReturnPolicy />} />
          <Route path="/comparative-advertising-disclaimer" element={<ComparativeAdvertisingDisclaimer />} />
          {/* Hidden secure admin routes - not publicly accessible */}
          <Route path="/sp-secure-admin-access-2024" element={<AdminLogin />} />
          <Route path="/a2025" element={<AdminLogin />} />
          <Route path="/admin/a2025" element={<AdminLogin />} />
          <Route path="/admin/setup" element={<AdminSetup />} />
          
          <Route path="/brand-manager" element={<BrandManager />} />
          <Route path="/branding-manager" element={<BrandingManager />} />
          <Route path="/music" element={<MusicVideoGrid />} />
          <Route path="/video-performances" element={<VideoPerformancesPage />} />
          <Route path="/brands" element={<PopularBrands />} />
          <Route path="/brands/:brandId" element={<BrandCollection />} />
          <Route path="/ernie-ball-collection" element={<ErnieBallCollection />} />
          <Route path="/daddario-collection" element={<DAddarioCollection />} />
          <Route path="/elixir-collection" element={<ElixirCollection />} />
          <Route path="/ernie-strings" element={<UkuleleStringsPage />} />
          <Route path="/string-finder" element={<StringFinderQuiz />} />
          <Route path="/string-finder-demo" element={<StringFinderDemo />} />
          <Route path="/mix-and-match" element={<BuildYourSet />} />
          <Route path="/user-dashboard" element={<UserDashboard />} />
          
          {/* Customer Messages */}
          <Route 
            path="/messages" 
            element={
              <ProtectedRoute>
                <CustomerMessages />
              </ProtectedRoute>
            } 
          />
          
          {/* Customer Chat */}
          <Route 
            path="/chat" 
            element={
              <ProtectedRoute>
                <CustomerChat />
              </ProtectedRoute>
            } 
          />
          
          {/* Customer Orders */}
          <Route 
            path="/orders" 
            element={
              <ProtectedRoute>
                <CustomerOrders />
              </ProtectedRoute>
            } 
          />
          
          {/* Customer Wishlist */}
          <Route 
            path="/wishlist" 
            element={
              <ProtectedRoute>
                <Wishlist />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin Routes - Only show if NOT in customer-only mode */}
          {!isCustomerOnlyMode && (
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requiredRole="admin" fallbackPath="/admin/login">
                  <AdminLayout />
                </ProtectedRoute>
              } 
            >
              {/* Admin Dashboard */}
              <Route index element={<ShopifyDashboard />} />
              <Route path="dashboard" element={<ShopifyDashboard />} />
              
              {/* Product Management */}
              <Route path="products" element={<ProductManagement />} />
              <Route path="product-manager" element={<ProductManager />} />
              <Route path="manage-active-products" element={<ManageActiveProducts />} />
              <Route path="category-manager" element={<CategoryManager />} />
              <Route path="holiday-specials" element={<HolidaySpecialsManager />} />
              <Route path="black-friday" element={<Navigate to="/admin/holiday-specials" replace />} />
              <Route path="merch" element={<MerchManagement />} />
              
              {/* Customer Management */}
              <Route path="customers" element={<AdminDashboard />} />
              <Route path="customer-management" element={<CustomerManagement />} />
              <Route path="automated-messaging" element={<AutomatedMessaging />} />
              <Route path="customer-messaging" element={<CustomerMessaging />} />
              <Route path="customer-order-history" element={<CustomerOrderHistory />} />
              
              {/* Inventory Management */}
              <Route path="inventory" element={<EnhancedInventoryManager />} />
              
              {/* Analytics */}
              <Route path="sales-analytics" element={<SalesAnalyticsDashboard />} />
              <Route path="enhanced-analytics" element={<EnhancedAnalytics />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              
              {/* Order & Tracking */}
              <Route path="order-management" element={<OrderManagement />} />
              <Route path="order-tracking-management" element={<AdminTrackingManagement />} />
              <Route path="tracking-management" element={<AdminTrackingManagement />} />
              
              {/* Shipping */}
              <Route path="shipping" element={<ShippingManagement />} />
              
              {/* Communication */}
              <Route path="messages" element={<AdminDashboard />} />
              <Route path="chat" element={<AdminChat />} />
              <Route path="notifications" element={<AdminNotificationCenter />} />
              <Route path="enhanced-notifications" element={<EnhancedNotificationSystem />} />
              
              {/* Branding */}
              <Route path="branding" element={<BrandingManager />} />
              <Route path="branding-manager" element={<BrandingManager />} />
              
              {/* Music */}
              <Route path="music" element={<MusicAdminPage />} />
              
              {/* Marketing */}
              <Route path="marketing" element={<AdminDashboard />} />
              <Route path="flash-sales" element={<FlashSalesManager />} />
              <Route path="volume-pricing" element={<VolumePricingManager />} />
              <Route path="discount-management" element={<DiscountManagement />} />
              
              {/* Gifts */}
              <Route path="gifts" element={<GiftManager />} />
              
              {/* Bundle Deals */}
              <Route path="bundle-deals" element={<BundleDealsManager />} />
              
              {/* Video Performances (homepage showcase) */}
              <Route path="video-performances" element={<VideoPerformanceManager />} />
              
              {/* Mix and Match */}
              <Route path="mix-and-match" element={<MixAndMatchManagement />} />
              
              {/* Mega Menu Management */}
              <Route path="mega-menu" element={<MegaMenuManager />} />
              
              {/* A/B Testing */}
              <Route path="ab-testing" element={<ABTestAdmin />} />
              
              {/* Storyboards */}
              <Route path="storyboards" element={<StoryboardManager />} />
              <Route path="prompt-library" element={<PromptLibraryAdmin />} />
              
              {/* System */}
              <Route path="monitoring" element={<AdminSystemMonitor />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="settings-panel" element={<AdminSettingsPanel />} />
              <Route path="coming-soon" element={<ComingSoonAdmin />} />
              <Route path="maintenance" element={<MaintenanceAdminPage />} />
              
              {/* Payment Configuration */}
              <Route path="payment-config" element={<PaymentConfigManager />} />
            </Route>
          )}
          
          {/* Additional routes can be added here */}
          <Route path="*" element={<AuthPage />} />
        </Routes>
      </Suspense>
      
      {/* Footer - Show on all non-admin routes */}
      {!isAdminRoute && !isAdminAuthRoute && <Footer />}
      </CookieConsentProvider>
    </div>
  );
};

function CartRedirect() {
  const { setSideCartOpen } = useCart();
  const navigate = useNavigate();
  useEffect(() => {
    setSideCartOpen(true);
    navigate('/', { replace: true });
  }, [setSideCartOpen, navigate]);
  return null;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <PushNotificationProvider>
            <UserPreferencesProvider>
              <MaintenanceProvider>
                <ViewModeProvider>
                  <HeroProvider>
                    <FavoritesProvider>
                      <CartAnimationProvider>
                        <ToastProvider>
                          <CartProvider>
                            <CustomerProvider>
                              <CurrencyProvider>
                                <WishlistProvider>
                                  <LocalizationProvider>
                                    <DevelopmentProvider>
                                      <ComingSoonProvider>
                                        <AppContent />
                                      </ComingSoonProvider>
                                    </DevelopmentProvider>
                                  </LocalizationProvider>
                                </WishlistProvider>
                              </CurrencyProvider>
                            </CustomerProvider>
                          </CartProvider>
                        </ToastProvider>
                      </CartAnimationProvider>
                    </FavoritesProvider>
                  </HeroProvider>
                </ViewModeProvider>
              </MaintenanceProvider>
            </UserPreferencesProvider>
          </PushNotificationProvider>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App; 
